import { getPageProps } from "@/utils/dataLayer";
import { z } from "zod";
import { ExerciseData } from "./exerciseSchema";

// Type definitions for TypeScript
type StringReturnOp =
    | GetStringDataOp
    | ArrayItemOp<StringArrayReturnOp>
    | ConcatenateOp
    | JoinOp
    | IfOp<string>;

type NumberReturnOp =
    | GetNumberDataOp
    | StringLengthOp
    | ArrayLengthOp
    | ArrayItemOp<NumberArrayReturnOp>
    | IfOp<number>;

type BooleanReturnOp =
    | GetBooleanDataOp
    | IsUserTypeOp
    | AndOp
    | OrOp
    | NotOp
    | EqualOp
    | NotEqualOp
    | GreaterThanOp
    | GreaterThanOrEqualOp
    | LessThanOp
    | LessThanOrEqualOp
    | ArrayContainsOp
    | ArrayItemOp<BooleanArrayReturnOp>
    | IfOp<boolean>;

type StringArrayReturnOp =
    | GetStringArrayDataOp
    | ArraySliceOp<string>
    | ArrayConcatOp<string>
    | IfOp<string[]>;
type NumberArrayReturnOp =
    | GetNumberArrayDataOp
    | ArraySliceOp<number>
    | ArrayConcatOp<number>
    | IfOp<number[]>;
type BooleanArrayReturnOp =
    | GetBooleanArrayDataOp
    | ArraySliceOp<boolean>
    | ArrayConcatOp<boolean>
    | IfOp<boolean[]>;

type AnyReturnOp =
    | StringReturnOp
    | NumberReturnOp
    | BooleanReturnOp
    | PrimitiveArrayReturnOp;

type Primitive = string | number | boolean;

type PrimitiveReturnOp = StringReturnOp | NumberReturnOp | BooleanReturnOp;

type PrimitiveArray =
    | (string | StringReturnOp)[]
    | (number | NumberReturnOp)[]
    | (boolean | BooleanReturnOp)[];

type PrimitiveArrayReturnOp =
    | StringArrayReturnOp
    | NumberArrayReturnOp
    | BooleanArrayReturnOp;

// Operation types
type IsUserTypeOp = ["isUserType", "client" | "coach"];
type AndOp = ["and", ...(BooleanReturnOp | boolean)[]];
type OrOp = ["or", ...(BooleanReturnOp | boolean)[]];
type NotOp = ["not", BooleanReturnOp];
type EqualOp = [
    "equal",
    PrimitiveReturnOp | Primitive,
    PrimitiveReturnOp | Primitive,
];
type NotEqualOp = [
    "notEqual",
    PrimitiveReturnOp | Primitive,
    PrimitiveReturnOp | Primitive,
];
type GreaterThanOp = [
    "greaterThan",
    NumberReturnOp | number,
    NumberReturnOp | number,
];
type GreaterThanOrEqualOp = [
    "greaterThanOrEqual",
    NumberReturnOp | number,
    NumberReturnOp | number,
];
type LessThanOp = [
    "lessThan",
    NumberReturnOp | number,
    NumberReturnOp | number,
];
type LessThanOrEqualOp = [
    "lessThanOrEqual",
    NumberReturnOp | number,
    NumberReturnOp | number,
];
type GetStringDataOp = ["getStringData", string];
type GetNumberDataOp = ["getNumberData", string];
type GetBooleanDataOp = ["getBooleanData", string];
type GetStringArrayDataOp = ["getStringArrayData", string];
type GetNumberArrayDataOp = ["getNumberArrayData", string];
type GetBooleanArrayDataOp = ["getBooleanArrayData", string];
type StringLengthOp = ["stringLength", StringReturnOp];
type ArrayLengthOp = ["arrayLength", PrimitiveArrayReturnOp];
type ArrayContainsOp = [
    "arrayContains",
    PrimitiveArrayReturnOp,
    ...(PrimitiveReturnOp | Primitive)[],
];
type ArrayItemOp<T extends PrimitiveArrayReturnOp> = [
    "arrayItem",
    T,
    NumberReturnOp | number,
];
type ConcatenateOp = ["concatenate", ...(PrimitiveReturnOp | Primitive)[]];
type JoinOp = ["join", StringReturnOp | string, StringArrayReturnOp | string[]];
type ArraySliceOp<T extends string | number | boolean> = [
    "arraySlice",
    T extends string
        ? StringArrayReturnOp | string[]
        : T extends number
          ? NumberArrayReturnOp | number[]
          : BooleanArrayReturnOp | boolean[],
    NumberReturnOp | number | undefined,
    NumberReturnOp | number | undefined,
];
type ArrayConcatOp<T extends string | number | boolean> = [
    "arrayConcat",
    T extends string
        ? StringArrayReturnOp | string[]
        : T extends number
          ? NumberArrayReturnOp | number[]
          : BooleanArrayReturnOp | boolean[],
    T extends string
        ? StringArrayReturnOp | string[]
        : T extends number
          ? NumberArrayReturnOp | number[]
          : BooleanArrayReturnOp | boolean[],
];
type IfOp<
    T extends string | number | boolean | string[] | number[] | boolean[],
> = [
    "if",
    BooleanReturnOp | boolean,
    T extends string
        ? StringReturnOp | string
        : T extends number
          ? NumberReturnOp | number
          : T extends boolean
            ? BooleanReturnOp | boolean
            : T extends string[]
              ? StringArrayReturnOp | string[]
              : T extends number[]
                ? NumberArrayReturnOp | number[]
                : BooleanArrayReturnOp | boolean[],
    T extends string
        ? StringReturnOp | string
        : T extends number
          ? NumberReturnOp | number
          : T extends boolean
            ? BooleanReturnOp | boolean
            : T extends string[]
              ? StringArrayReturnOp | string[]
              : T extends number[]
                ? NumberArrayReturnOp | number[]
                : BooleanArrayReturnOp | boolean[],
];

// Helper type to get the return type of operation
type OpReturnType<T> = T extends StringReturnOp
    ? string
    : T extends NumberReturnOp
      ? number
      : T extends BooleanReturnOp
        ? boolean
        : T extends StringArrayReturnOp
          ? string[]
          : T extends NumberArrayReturnOp
            ? number[]
            : T extends BooleanArrayReturnOp
              ? boolean[]
              : never;

type OpArrayReturnType<T> = T extends StringArrayReturnOp
    ? string[]
    : T extends (StringReturnOp | string)[]
      ? string[]
      : T extends NumberArrayReturnOp
        ? number[]
        : T extends (NumberReturnOp | number)[]
          ? number[]
          : T extends BooleanArrayReturnOp
            ? boolean[]
            : T extends (BooleanReturnOp | boolean)[]
              ? boolean[]
              : never;

type ResolveAnyReturnType<T> = T extends AnyReturnOp
    ? OpReturnType<T>
    : T extends PrimitiveArray | PrimitiveArrayReturnOp
      ? OpArrayReturnType<T>
      : T extends string
        ? string
        : T extends number
          ? number
          : T extends boolean
            ? boolean
            : T extends undefined
              ? undefined
              : never;

// Define the schemas for Zod validation
// We need to define these schemas as types first, then implement them using z.lazy()
type ZodPrimitiveType = z.ZodType<Primitive>;
type ZodAnyReturnOpType = z.ZodType<AnyReturnOp>;
type ZodStringReturnOpType = z.ZodType<StringReturnOp>;
type ZodNumberReturnOpType = z.ZodType<NumberReturnOp>;
type ZodBooleanReturnOpType = z.ZodType<BooleanReturnOp>;
type ZodPrimitiveReturnOpType = z.ZodType<PrimitiveReturnOp>;
type ZodStringArrayReturnOpType = z.ZodType<StringArrayReturnOp>;
type ZodNumberArrayReturnOpType = z.ZodType<NumberArrayReturnOp>;
type ZodBooleanArrayReturnOpType = z.ZodType<BooleanArrayReturnOp>;
type ZodPrimitiveArrayReturnOpType = z.ZodType<PrimitiveArrayReturnOp>;

// Create the schemas with proper recursive definitions
export const stringReturnOpSchema: ZodStringReturnOpType = z.lazy(() =>
    z.union([
        z.tuple([z.literal("getStringData"), z.string()]),
        z.tuple([
            z.literal("arrayItem"),
            stringArrayReturnOpSchema,
            z.union([numberReturnOpSchema, z.number()]),
        ]),
        z
            .tuple([z.literal("concatenate")])
            .rest(z.union([primitiveReturnOpSchema, primitiveSchema])),
        z.tuple([
            z.literal("join"),
            z.union([stringReturnOpSchema, z.string()]),
            z.union([stringArrayReturnOpSchema, z.array(z.string())]),
        ]),
        z.tuple([
            z.literal("if"),
            z.union([booleanReturnOpSchema, z.boolean()]),
            z.union([stringReturnOpSchema, z.string()]),
            z.union([stringReturnOpSchema, z.string()]),
        ]),
    ]),
);

export const numberReturnOpSchema: ZodNumberReturnOpType = z.lazy(() =>
    z.union([
        z.tuple([z.literal("getNumberData"), z.string()]),
        z.tuple([z.literal("stringLength"), stringReturnOpSchema]),
        z.tuple([z.literal("arrayLength"), primitiveArrayReturnOpSchema]),
        z.tuple([
            z.literal("arrayItem"),
            numberArrayReturnOpSchema,
            z.union([numberReturnOpSchema, z.number()]),
        ]),
        z.tuple([
            z.literal("if"),
            z.union([booleanReturnOpSchema, z.boolean()]),
            z.union([numberReturnOpSchema, z.number()]),
            z.union([numberReturnOpSchema, z.number()]),
        ]),
    ]),
);

export const booleanReturnOpSchema: ZodBooleanReturnOpType = z.lazy(() =>
    z.union([
        z.tuple([z.literal("getBooleanData"), z.string()]),
        z.tuple([
            z.literal("isUserType"),
            z.union([z.literal("client"), z.literal("coach")]),
        ]),
        z.tuple([z.literal("not"), z.lazy(() => booleanReturnOpSchema)]),
        z.tuple([
            z.literal("equal"),
            z.union([primitiveReturnOpSchema, primitiveSchema]),
            z.union([primitiveReturnOpSchema, primitiveSchema]),
        ]),
        z.tuple([
            z.literal("notEqual"),
            z.union([primitiveReturnOpSchema, primitiveSchema]),
            z.union([primitiveReturnOpSchema, primitiveSchema]),
        ]),
        z.tuple([
            z.literal("greaterThan"),
            z.union([numberReturnOpSchema, z.number()]),
            z.union([numberReturnOpSchema, z.number()]),
        ]),
        z.tuple([
            z.literal("greaterThanOrEqual"),
            z.union([numberReturnOpSchema, z.number()]),
            z.union([numberReturnOpSchema, z.number()]),
        ]),
        z.tuple([
            z.literal("lessThan"),
            z.union([numberReturnOpSchema, z.number()]),
            z.union([numberReturnOpSchema, z.number()]),
        ]),
        z.tuple([
            z.literal("lessThanOrEqual"),
            z.union([numberReturnOpSchema, z.number()]),
            z.union([numberReturnOpSchema, z.number()]),
        ]),
        z
            .tuple([z.literal("arrayContains"), primitiveArrayReturnOpSchema])
            .rest(
                z.lazy(() =>
                    z.union([primitiveReturnOpSchema, primitiveSchema]),
                ),
            ),
        z
            .tuple([z.literal("and")])
            .rest(z.lazy(() => z.union([booleanReturnOpSchema, z.boolean()]))),
        z
            .tuple([z.literal("or")])
            .rest(z.lazy(() => z.union([booleanReturnOpSchema, z.boolean()]))),
        z.tuple([
            z.literal("arrayItem"),
            booleanArrayReturnOpSchema,
            z.union([numberReturnOpSchema, z.number()]),
        ]),
        z.tuple([
            z.literal("if"),
            z.union([booleanReturnOpSchema, z.boolean()]),
            z.union([booleanReturnOpSchema, z.boolean()]),
            z.union([booleanReturnOpSchema, z.boolean()]),
        ]),
    ]),
);

export const primitiveSchema: ZodPrimitiveType = z.lazy(() =>
    z.union([z.string(), z.number(), z.boolean()]),
);

export const primitiveReturnOpSchema: ZodPrimitiveReturnOpType = z.lazy(() =>
    z.union([
        stringReturnOpSchema,
        numberReturnOpSchema,
        booleanReturnOpSchema,
    ]),
);

export const stringArrayReturnOpSchema: ZodStringArrayReturnOpType = z.lazy(
    () =>
        z.union([
            z.tuple([z.literal("getStringArrayData"), z.string()]),
            z.tuple([
                z.literal("arraySlice"),
                z.union([stringArrayReturnOpSchema, z.array(z.string())]),
                z.union([numberReturnOpSchema, z.number(), z.undefined()]),
                z.union([numberReturnOpSchema, z.number(), z.undefined()]),
            ]),
            z.tuple([
                z.literal("arrayConcat"),
                z.union([stringArrayReturnOpSchema, z.array(z.string())]),
                z.union([stringArrayReturnOpSchema, z.array(z.string())]),
            ]),
            z.tuple([
                z.literal("if"),
                z.union([booleanReturnOpSchema, z.boolean()]),
                z.union([stringArrayReturnOpSchema, z.array(z.string())]),
                z.union([stringArrayReturnOpSchema, z.array(z.string())]),
            ]),
        ]),
);

export const numberArrayReturnOpSchema: ZodNumberArrayReturnOpType = z.lazy(
    () =>
        z.union([
            z.tuple([z.literal("getNumberArrayData"), z.string()]),
            z.tuple([
                z.literal("arraySlice"),
                z.union([numberArrayReturnOpSchema, z.array(z.number())]),
                z.union([numberReturnOpSchema, z.number(), z.undefined()]),
                z.union([numberReturnOpSchema, z.number(), z.undefined()]),
            ]),
            z.tuple([
                z.literal("arrayConcat"),
                z.union([numberArrayReturnOpSchema, z.array(z.number())]),
                z.union([numberArrayReturnOpSchema, z.array(z.number())]),
            ]),
            z.tuple([
                z.literal("if"),
                z.union([booleanReturnOpSchema, z.boolean()]),
                z.union([numberArrayReturnOpSchema, z.array(z.number())]),
                z.union([numberArrayReturnOpSchema, z.array(z.number())]),
            ]),
        ]),
);

export const booleanArrayReturnOpSchema: ZodBooleanArrayReturnOpType = z.lazy(
    () =>
        z.union([
            z.tuple([z.literal("getBooleanArrayData"), z.string()]),
            z.tuple([
                z.literal("arraySlice"),
                z.union([booleanArrayReturnOpSchema, z.array(z.boolean())]),
                z.union([numberReturnOpSchema, z.number(), z.undefined()]),
                z.union([numberReturnOpSchema, z.number(), z.undefined()]),
            ]),
            z.tuple([
                z.literal("arrayConcat"),
                z.union([booleanArrayReturnOpSchema, z.array(z.boolean())]),
                z.union([booleanArrayReturnOpSchema, z.array(z.boolean())]),
            ]),
            z.tuple([
                z.literal("if"),
                z.union([booleanReturnOpSchema, z.boolean()]),
                z.union([booleanArrayReturnOpSchema, z.array(z.boolean())]),
                z.union([booleanArrayReturnOpSchema, z.array(z.boolean())]),
            ]),
        ]),
);

export const primitiveArrayReturnOpSchema: ZodPrimitiveArrayReturnOpType =
    z.lazy(() =>
        z.union([
            stringArrayReturnOpSchema,
            numberArrayReturnOpSchema,
            booleanArrayReturnOpSchema,
        ]),
    );

export const anyReturnOpSchema: ZodAnyReturnOpType = z.lazy(() =>
    z.union([
        stringReturnOpSchema,
        numberReturnOpSchema,
        booleanReturnOpSchema,
        primitiveArrayReturnOpSchema,
    ]),
);

// Implementation functions with proper return types
function isUserTypeFn(
    data: ExerciseData,
    op: IsUserTypeOp,
): OpReturnType<IsUserTypeOp> {
    const { user } = getPageProps();
    const [, userType] = op;
    return user?.type === userType;
}

function andFn(data: ExerciseData, op: AndOp): OpReturnType<AndOp> {
    const [, ...items] = op;
    return items.every((item) => resolve(data, item));
}

function orFn(data: ExerciseData, op: OrOp): OpReturnType<OrOp> {
    const [, ...items] = op;
    return items.some((item) => resolve(data, item));
}

function notFn(data: ExerciseData, op: NotOp): OpReturnType<NotOp> {
    const [, item] = op;
    return !resolve(data, item);
}

function equalFn(data: ExerciseData, op: EqualOp): OpReturnType<EqualOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) === resolve(data, item2);
}

function notEqualFn(
    data: ExerciseData,
    op: NotEqualOp,
): OpReturnType<NotEqualOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) !== resolve(data, item2);
}

function greaterThanFn(
    data: ExerciseData,
    op: GreaterThanOp,
): OpReturnType<GreaterThanOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) > resolve(data, item2);
}

function greaterThanOrEqualFn(
    data: ExerciseData,
    op: GreaterThanOrEqualOp,
): OpReturnType<GreaterThanOrEqualOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) >= resolve(data, item2);
}

function lessThanFn(
    data: ExerciseData,
    op: LessThanOp,
): OpReturnType<LessThanOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) < resolve(data, item2);
}

function lessThanOrEqualFn(
    data: ExerciseData,
    op: LessThanOrEqualOp,
): OpReturnType<LessThanOrEqualOp> {
    const [, item1, item2] = op;
    return resolve(data, item1) <= resolve(data, item2);
}

function getStringDataFn(
    data: ExerciseData,
    op: GetStringDataOp,
): OpReturnType<GetStringDataOp> {
    const [, path] = op;
    return String(path.split(".").reduce((acc, key) => acc && acc[key], data));
}

function getNumberDataFn(
    data: ExerciseData,
    op: GetNumberDataOp,
): OpReturnType<GetNumberDataOp> {
    const [, path] = op;
    return Number(path.split(".").reduce((acc, key) => acc && acc[key], data));
}

function getBooleanDataFn(
    data: ExerciseData,
    op: GetBooleanDataOp,
): OpReturnType<GetBooleanDataOp> {
    const [, path] = op;
    return Boolean(path.split(".").reduce((acc, key) => acc && acc[key], data));
}

function getArrayDataFn(
    data: ExerciseData,
    op: GetStringArrayDataOp | GetNumberArrayDataOp | GetBooleanArrayDataOp,
): Primitive[] {
    const [, path] = op;
    const array = path
        .split(".")
        .reduce((acc, key) => acc && acc[key], data) as unknown as (
        | string
        | number
        | boolean
    )[];
    if (array === undefined) {
        // Consider unanswered as an empty array
        return [];
    }
    if (!Array.isArray(array)) {
        throw new Error(
            `Array expected at path ${path}, got ${array} of type ${typeof array}`,
        );
    }
    return array;
}

function getStringArrayDataFn(
    data: ExerciseData,
    op: GetStringArrayDataOp,
): OpReturnType<GetStringArrayDataOp> {
    return getArrayDataFn(data, op).map((item) => String(item));
}

function getNumberArrayDataFn(
    data: ExerciseData,
    op: GetNumberArrayDataOp,
): OpReturnType<GetNumberArrayDataOp> {
    return getArrayDataFn(data, op).map((item) => Number(item));
}

function getBooleanArrayDataFn(
    data: ExerciseData,
    op: GetBooleanArrayDataOp,
): OpReturnType<GetBooleanArrayDataOp> {
    return getArrayDataFn(data, op).map((item) => Boolean(item));
}

function stringLengthFn(
    data: ExerciseData,
    op: StringLengthOp,
): OpReturnType<StringLengthOp> {
    const [, path] = op;
    return String(resolve(data, path)).length;
}

function arrayLengthFn(
    data: ExerciseData,
    op: ArrayLengthOp,
): OpReturnType<ArrayLengthOp> {
    const [, arrayOp] = op;
    return resolve(data, arrayOp).length;
}

function arrayContainsFn(
    data: ExerciseData,
    op: ArrayContainsOp,
): OpReturnType<ArrayContainsOp> {
    const [, arrayOp, ...targetOps] = op;
    const array = resolve(data, arrayOp);

    return targetOps.some((targetOp) => {
        const targetValue = resolve(data, targetOp);
        return array.some((item: Primitive) => item === targetValue);
    });
}

function arrayItemFn<T extends PrimitiveArrayReturnOp>(
    data: ExerciseData,
    op: ArrayItemOp<T>,
): OpReturnType<ArrayItemOp<T>> {
    const [, arrayOp, index] = op;
    const array = resolve(data, arrayOp);
    const indexValue = resolve(data, index);
    return array[indexValue] as OpReturnType<ArrayItemOp<T>>;
}

function concatenateFn(
    data: ExerciseData,
    op: ConcatenateOp,
): OpReturnType<ConcatenateOp> {
    const [, ...items] = op;
    return items.map((item) => resolve(data, item)).join("");
}

function joinFn(data: ExerciseData, op: JoinOp): OpReturnType<JoinOp> {
    const [, separatorOp, arrayOp] = op;
    const separator = resolve(data, separatorOp);
    const array = resolve(data, arrayOp);
    return array.join(separator);
}

function arraySliceFn<T extends string | number | boolean>(
    data: ExerciseData,
    op: ArraySliceOp<T>,
): OpArrayReturnType<T> {
    const [, arrayOp, start, end] = op;
    const array = resolve(data, arrayOp) as string[] | number[] | boolean[];
    const startValue = resolve(data, start);
    const endValue = resolve(data, end);
    return array.slice(startValue, endValue) as OpArrayReturnType<T>;
}

function arrayConcatFn<T extends string | number | boolean>(
    data: ExerciseData,
    op: ArrayConcatOp<T>,
): OpArrayReturnType<T> {
    const [, arrayOp1, arrayOp2] = op;
    const array1 = resolve(data, arrayOp1) as string[] | number[] | boolean[];
    const array2 = resolve(data, arrayOp2) as string[] | number[] | boolean[];
    return array1.concat(array2) as OpArrayReturnType<T>;
}

function ifFn<
    T extends string | number | boolean | string[] | number[] | boolean[],
>(data: ExerciseData, op: IfOp<T>): ResolveAnyReturnType<T> {
    const [, conditionOp, trueScenarioOp, falseScenarioOp] = op;
    const condition = resolve(data, conditionOp);
    if (condition) {
        return resolve(
            data,
            trueScenarioOp,
        ) as unknown as ResolveAnyReturnType<T>;
    } else {
        return resolve(
            data,
            falseScenarioOp,
        ) as unknown as ResolveAnyReturnType<T>;
    }
}

const OP_MAP: Record<
    AnyReturnOp[0],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: ExerciseData, op: any) => OpReturnType<any>
> = {
    isUserType: isUserTypeFn,
    and: andFn,
    or: orFn,
    not: notFn,
    equal: equalFn,
    notEqual: notEqualFn,
    greaterThan: greaterThanFn,
    greaterThanOrEqual: greaterThanOrEqualFn,
    lessThan: lessThanFn,
    lessThanOrEqual: lessThanOrEqualFn,
    getStringData: getStringDataFn,
    getNumberData: getNumberDataFn,
    getBooleanData: getBooleanDataFn,
    getStringArrayData: getStringArrayDataFn,
    getNumberArrayData: getNumberArrayDataFn,
    getBooleanArrayData: getBooleanArrayDataFn,
    stringLength: stringLengthFn,
    arrayLength: arrayLengthFn,
    arrayContains: arrayContainsFn,
    arrayItem: arrayItemFn,
    concatenate: concatenateFn,
    join: joinFn,
    arraySlice: arraySliceFn,
    arrayConcat: arrayConcatFn,
    if: ifFn,
};

/**
 * Handles recursive resolution of operations.
 */
export function resolveOp<T extends AnyReturnOp>(
    data: ExerciseData,
    op: T,
): OpReturnType<T> {
    const opName = op[0];

    const opFn = OP_MAP[opName];

    if (!opFn) {
        throw new Error(`Unknown operation: ${opName}`);
    }

    return opFn(data, op) as OpReturnType<T>;
}

export function resolve<
    T extends
        | Primitive
        | AnyReturnOp
        | PrimitiveArray
        | PrimitiveArrayReturnOp
        | undefined,
>(data: ExerciseData, op: T): ResolveAnyReturnType<T> {
    if (
        // This is a performance optimization to avoid validating the op if it is a primitive.
        typeof op === "string" ||
        typeof op === "number" ||
        typeof op === "boolean" ||
        typeof op === "undefined"
    ) {
        return op as unknown as ResolveAnyReturnType<T>;
    }

    /**
     * This is a quick way to check if the op is a valid operation. It would be better to check
     * against the anyReturnOpSchema, but that is too slow as things become deeply nested.
     */
    if (Array.isArray(op) && typeof op[0] === "string" && op[0] in OP_MAP) {
        return resolveOp(data, op as AnyReturnOp) as ResolveAnyReturnType<T>;
    }
    if (Array.isArray(op)) {
        return op.map((item) => resolve(data, item)) as ResolveAnyReturnType<T>;
    }
    return op as unknown as ResolveAnyReturnType<T>;
}
